import React from 'react';

import { Stack } from '@mantine/core';
import type { PlotlyTypes } from 'visyn_core/plotly';
import { PlotlyComponent } from 'visyn_core/plotly';

import type { HeatmapData } from '../generated/generatedOpenAPI';

interface HeatmapProps {
  heatmapData: HeatmapData | undefined;
}

export default function Heatmap({ heatmapData }: HeatmapProps) {
  const [data, xVals, yVals] = React.useMemo(() => {
    if (!heatmapData) {
      return [];
    }

    const uniqueXSet = new Set(heatmapData.vhGene);
    const uniqueX = Array.from(uniqueXSet.values()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const uniqueYSet = new Set(heatmapData.vlGene);
    const uniqueY = Array.from(uniqueYSet.values()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    return [
      [
        {
          z: heatmapData.size,
          x: heatmapData.vhGene,
          y: heatmapData.vlGene,
          type: 'heatmap',
          hoverongaps: false,
          hoverinfo: 'x',
          hovertemplate: `VH</b>: %{x}<br>VL</b>: %{y}<br>Count</b>: %{z} <extra></extra>`,
          colorscale: [
            [0, '#003367'],
            [0.2, '#2e72ae'],
            [0.4, '#77b5ea'],
            [0.5, '#f5f5f5'],
            [0.6, '#f99761'],
            [0.8, '#c2451a'],
            [1, '#6f0000'],
          ],
        },
      ],
      uniqueX,
      uniqueY,
    ];
  }, [heatmapData]);

  return (
    <Stack gap={0} align="center" data-testid="v-gene-heatmap">
      <PlotlyComponent
        useResizeHandler
        style={{ width: '100%' }}
        data={data as PlotlyTypes.Data[]}
        layout={{
          title: {
            text: 'Heatmap of V-gene usage',
            font: {
              size: 20,
              weight: 500,
            },
            y: 0.992,
          },
          height: 650,
          margin: {
            l: 100,
            r: 40,
            b: 110,
            t: 30,
          },
          yaxis: {
            type: 'category',
            autorange: true,
            title: {
              text: 'Light V gene',
              font: { size: 16 },
            },
            categoryarray: yVals,
            categoryorder: 'array',
            layer: 'above traces',
            tickvals: yVals,
            automargin: true,
          },
          xaxis: {
            type: 'category',
            autorange: true,
            title: {
              text: 'Heavy V gene',
              font: { size: 16 },
            },
            categoryarray: xVals,
            categoryorder: 'array',
            layer: 'above traces',
            tickmode: 'array',
            tickvals: xVals,
            automargin: true,
          },
        }}
        config={{
          displaylogo: false,
          modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'autoScale2d', 'select2d'],
        }}
      />
    </Stack>
  );
}
